import i18n from "../i18n";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatCurrency, formatNumber } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    HardHat, Plus, Pencil, Trash2, Wrench, Users2, ClipboardList,
    AlertTriangle, Calendar as CalendarIcon, DollarSign, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { farmDateIso, parseFarmCalendarValue } from "@/lib/farmTime";

const PROJECT_STATUS = [
    { key: "planning", label: "Planning", color: "bg-slate-200 text-slate-700" },
    { key: "in_progress", label: "In Progress", color: "bg-blue-500/15 text-blue-700 border-blue-500/40" },
    { key: "on_hold", label: "On Hold", color: "bg-amber-500/15 text-amber-700 border-amber-500/40" },
    { key: "completed", label: "Completed", color: "bg-primary/15 text-primary border-primary/40" },
    { key: "cancelled", label: "Cancelled", color: "bg-destructive/15 text-destructive border-destructive/40" },
];
const PROJECT_TYPES = ["new_build", "expansion", "renovation", "repair", "landscaping", "other"];
const ASSET_TYPES = ["building", "other"];
const ASSET_CONDITION = ["excellent", "good", "fair", "poor", "needs_repair"];
const ASSET_STATUS = [
    { key: "in_use", label: "In Use", color: "bg-primary/15 text-primary border-primary/40" },
    { key: "idle", label: "Idle", color: "bg-slate-200 text-slate-700 border-slate-400/40" },
    { key: "in_repair", label: "In Repair", color: "bg-amber-500/15 text-amber-700 border-amber-500/40" },
    { key: "on_loan", label: "On Loan", color: "bg-blue-500/15 text-blue-700 border-blue-500/40" },
    { key: "retired", label: "Retired", color: "bg-secondary text-muted-foreground border-border" },
    { key: "lost", label: "Lost", color: "bg-destructive/15 text-destructive border-destructive/40" },
];
const EXPENSE_CATEGORIES = ["materials", "labor", "equipment_rental", "permits", "transport", "misc"];

const statusMeta = (k) => PROJECT_STATUS.find((s) => s.key === k) || PROJECT_STATUS[0];
const assetStatusMeta = (k) => ASSET_STATUS.find((s) => s.key === (k || "in_use")) || ASSET_STATUS[0];
const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    const today = parseFarmCalendarValue(farmDateIso());
    const d = parseFarmCalendarValue(dateStr);
    if (!today || !d) return null;
    return Math.round((d - today) / 86400000);
};
const submissionId = (prefix) => `${prefix}-${farmDateIso().replace(/-/g, "")}-${crypto.randomUUID()}`;

export default function ConstructionPage() {
    const [projects, setProjects] = useState([]);
    const [assets, setAssets] = useState([]);
    const [contractors, setContractors] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [updates, setUpdates] = useState([]);
    const [summary, setSummary] = useState(null);
    const [farm, setFarm] = useState({ currency: "PHP" });

    const [projectDialog, setProjectDialog] = useState(null); // {id?, ...}
    const [assetDialog, setAssetDialog] = useState(null);
    const [contractorDialog, setContractorDialog] = useState(null);
    const [expenseDialog, setExpenseDialog] = useState(null);
    const [updateDialog, setUpdateDialog] = useState(null);
    const [projectDetail, setProjectDetail] = useState(null);

    const load = useCallback(async () => {
        try {
            const [p, a, c, e, u, s, f] = await Promise.all([
                api.get("/construction-projects").then((r) => r.data),
                api.get("/farm-assets").then((r) => r.data),
                api.get("/contractors").then((r) => r.data),
                api.get("/project-expenses").then((r) => r.data),
                api.get("/project-updates").then((r) => r.data),
                api.get("/construction/summary").then((r) => r.data),
                api.get("/farm").then((r) => r.data),
            ]);
            setProjects(p || []);
            // Vehicles → Fleet, machinery/tools/electrical/plumbing → Machinery & Equipment.
            // Construction now shows only true infrastructure (buildings, other).
            setAssets((a || []).filter((x) => x.type === "building" || x.type === "other" || x.type == null));
            setContractors(c || []);
            setExpenses(e || []); setUpdates(u || []); setSummary(s); setFarm(f || { currency: "PHP" });
        } catch (err) {
            console.error("Construction load failed", err);
            toast.error("Failed to load construction data");
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const cur = farm.currency || "PHP";
    const spentByProject = summary?.spent_by_project || {};
    const contractorMap = useMemo(
        () => Object.fromEntries(contractors.map((c) => [c.id, c])), [contractors]
    );

    // ---------- SAVE handlers ----------
    const saveDoc = async (path, body, dialogSetter) => {
        try {
            if (body.id) {
                const { id, ...rest } = body;
                await api.put(`/${path}/${id}`, rest);
                toast.success("Updated");
            } else {
                body.source_submission_id = body.source_submission_id || submissionId(path);
                await api.post(`/${path}`, body);
                toast.success("Created");
            }
            dialogSetter(null);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };
    const deleteDoc = async (path, id, label) => {
        if (!window.confirm(`Delete ${label}?`)) return;
        try {
            await api.delete(`/${path}/${id}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-6" data-testid="construction-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="tiny-label flex items-center gap-2"><HardHat className="w-4 h-4" /> {i18n.t("final.de4fc5601204")}</div>
                    <h1 className="text-3xl font-bold mt-1">{i18n.t("final.24a46c6bc0d7")}</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {i18n.t("final.4f68eeac77e4")}
                    </p>
                </div>
            </div>

            {/* KPI ROW */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="construction-kpi-row">
                    <KpiCard icon={ClipboardList} label={i18n.t("final.8eadc26886a5")} value={formatNumber(summary.kpis.active_projects)} testid="kpi-active-projects" tone="blue" />
                    <KpiCard icon={TrendingUp} label={i18n.t("final.1798b3ba42ee")} value={formatNumber(summary.kpis.completed_projects)} testid="kpi-completed-projects" tone="green" />
                    <KpiCard icon={DollarSign} label={i18n.t("final.9cbf868b72bc")} value={formatCurrency(summary.kpis.total_budget, cur, { compact: true })} testid="kpi-total-budget" tone="slate" />
                    <KpiCard icon={DollarSign} label={i18n.t("final.ccf67dadebe3")} value={formatCurrency(summary.kpis.total_spent, cur, { compact: true })} testid="kpi-total-spent" tone="amber" />
                    <KpiCard icon={Wrench} label={i18n.t("final.20e338624cee")} value={formatNumber(summary.kpis.asset_count)} testid="kpi-assets" tone="slate" sub={[
                        summary.kpis.assets_in_repair ? `${summary.kpis.assets_in_repair} in repair` : null,
                        summary.kpis.assets_idle ? `${summary.kpis.assets_idle} idle` : null,
                    ].filter(Boolean).join(" · ") || undefined} />
                    <KpiCard icon={AlertTriangle} label={i18n.t("final.346b137aaeb4")} value={formatNumber(summary.kpis.overdue_service_count)} testid="kpi-overdue-service" tone={summary.kpis.overdue_service_count > 0 ? "red" : "slate"} />
                </div>
            )}

            {/* Alerts banner */}
            {summary && (summary.kpis.overdue_service_count > 0 || summary.kpis.overbudget_count > 0) && (
                <Card className="p-4 border-destructive/30 bg-destructive/5" data-testid="construction-alerts">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                        <div className="flex-1 text-sm">
                            {summary.kpis.overdue_service_count > 0 && (
                                <div><b>{summary.kpis.overdue_service_count}</b> {i18n.t("final.c4ebb2c814de")} <b>{i18n.t("final.65004e4ff3fd")}</b>.</div>
                            )}
                            {summary.kpis.overbudget_count > 0 && (
                                <div><b>{summary.kpis.overbudget_count}</b> {i18n.t("final.4c10f68d1ec6")} <b>{i18n.t("final.ac154da76d74")}</b>.</div>
                            )}
                        </div>
                    </div>
                </Card>
            )}

            <Tabs defaultValue="projects">
                <TabsList data-testid="construction-tabs">
                    <TabsTrigger value="projects" data-testid="tab-projects"><ClipboardList className="w-4 h-4 mr-1" />{i18n.t("final.53e890d5f0ff")}</TabsTrigger>
                    <TabsTrigger value="assets" data-testid="tab-assets"><Wrench className="w-4 h-4 mr-1" />{i18n.t("final.31e1ca3152df")}</TabsTrigger>
                    <TabsTrigger value="contractors" data-testid="tab-contractors"><Users2 className="w-4 h-4 mr-1" />{i18n.t("final.373a53a12353")}</TabsTrigger>
                    <TabsTrigger value="schedule" data-testid="tab-schedule"><CalendarIcon className="w-4 h-4 mr-1" />{i18n.t("final.1a3c52bfd713")}</TabsTrigger>
                </TabsList>

                {/* ---------- PROJECTS TAB ---------- */}
                <TabsContent value="projects" className="space-y-4">
                    <div className="flex justify-end">
                        <Button onClick={() => setProjectDialog({})} data-testid="new-project-btn">
                            <Plus className="w-4 h-4 mr-1" />{i18n.t("final.fd57e65b2e31")}
                        </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {projects.length === 0 && (
                            <Card className="p-8 text-center text-sm text-muted-foreground md:col-span-2 xl:col-span-3">
                                {i18n.t("final.9d579ae4103f")}
                            </Card>
                        )}
                        {projects.map((p) => {
                            const spent = spentByProject[p.id] || 0;
                            const budget = Number(p.budget) || 0;
                            const pct = budget > 0 ? Math.min(100, (spent / budget) * 100) : 0;
                            const over = budget > 0 && spent > budget;
                            const st = statusMeta(p.status);
                            const dLeft = daysUntil(p.target_date);
                            return (
                                <Card key={p.id} className="p-4 space-y-3 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setProjectDetail(p)} data-testid={`project-card-${p.id}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-semibold truncate">{p.name}</div>
                                            <div className="text-xs text-muted-foreground truncate">{p.location || "—"} · {p.type || "project"}</div>
                                        </div>
                                        <Badge variant="outline" className={st.color}>{st.label}</Badge>
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between text-xs mb-1">
                                            <span className="text-muted-foreground">{i18n.t("final.7aeba4cd15b1")}</span>
                                            <span className={`font-semibold ${over ? "text-destructive" : ""}`}>
                                                {formatCurrency(spent, cur, { compact: true })} / {formatCurrency(budget, cur, { compact: true })}
                                            </span>
                                        </div>
                                        <Progress value={pct} className={over ? "[&>*]:bg-destructive" : ""} />
                                    </div>
                                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                                        <span className="inline-flex items-center gap-1"><CalendarIcon className="w-3 h-3" />{p.target_date || "no target"}</span>
                                        {dLeft != null && (
                                            <span className={dLeft < 0 ? "text-destructive font-semibold" : "font-mono"}>
                                                {dLeft < 0 ? `${-dLeft}d overdue` : `${dLeft}d left`}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex gap-2 pt-1">
                                        <Button size="sm" variant="outline" className="flex-1" onClick={(e) => { e.stopPropagation(); setProjectDialog(p); }} data-testid={`edit-project-${p.id}`}>
                                            <Pencil className="w-3.5 h-3.5 mr-1" />{i18n.t("final.5301648dcf6b")}
                                        </Button>
                                        <Button size="sm" variant="outline" className="text-destructive" onClick={(e) => { e.stopPropagation(); deleteDoc("construction-projects", p.id, p.name); }} data-testid={`delete-project-${p.id}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                </TabsContent>

                {/* ---------- ASSETS TAB ---------- */}
                <TabsContent value="assets" className="space-y-4">
                    <AssetsTabContent
                        assets={assets}
                        summary={summary}
                        cur={cur}
                        onNew={() => setAssetDialog({})}
                        onEdit={setAssetDialog}
                        onDelete={(a) => deleteDoc("farm-assets", a.id, a.name)}
                        onQuickStatus={async (a, next) => {
                            try {
                                await api.put(`/farm-assets/${a.id}`, { ...a, status: next });
                                toast.success(`${a.name} → ${assetStatusMeta(next).label}`);
                                load();
                            } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
                        }}
                    />
                </TabsContent>

                {/* ---------- CONTRACTORS TAB ---------- */}
                <TabsContent value="contractors" className="space-y-4">
                    <div className="flex justify-end">
                        <Button onClick={() => setContractorDialog({})} data-testid="new-contractor-btn">
                            <Plus className="w-4 h-4 mr-1" />{i18n.t("final.ad166ddcd6ce")}
                        </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {contractors.length === 0 && (
                            <Card className="p-8 text-center text-sm text-muted-foreground md:col-span-2 xl:col-span-3">
                                {i18n.t("final.1d661af9ea42")}
                            </Card>
                        )}
                        {contractors.map((c) => {
                            const paidTo = expenses
                                .filter((e) => e.contractor_id === c.id)
                                .reduce((s, e) => s + Number(e.amount || 0), 0);
                            return (
                                <Card key={c.id} className="p-4 space-y-2" data-testid={`contractor-card-${c.id}`}>
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <div className="font-semibold">{c.name}</div>
                                            <div className="text-xs text-muted-foreground">{c.specialty || "General"}</div>
                                        </div>
                                        {c.rating && <Badge>{"★".repeat(Math.min(5, c.rating))}</Badge>}
                                    </div>
                                    <div className="text-xs text-muted-foreground space-y-0.5">
                                        {c.phone && <div>📞 {c.phone}</div>}
                                        {c.email && <div>✉ {c.email}</div>}
                                    </div>
                                    <div className="text-xs pt-1 border-t border-border">
                                        {i18n.t("final.018fa09eb9c5")} <span className="font-semibold">{formatCurrency(paidTo, cur)}</span>
                                    </div>
                                    <div className="flex gap-2 pt-1">
                                        <Button size="sm" variant="outline" className="flex-1" onClick={() => setContractorDialog(c)} data-testid={`edit-contractor-${c.id}`}>
                                            <Pencil className="w-3.5 h-3.5 mr-1" />{i18n.t("final.5301648dcf6b")}
                                        </Button>
                                        <Button size="sm" variant="outline" className="text-destructive" onClick={() => deleteDoc("contractors", c.id, c.name)} data-testid={`delete-contractor-${c.id}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                </TabsContent>

                {/* ---------- MAINTENANCE SCHEDULE TAB ---------- */}
                <TabsContent value="schedule" className="space-y-4">
                    <Card className="p-4" data-testid="maintenance-schedule-card">
                        <div className="font-semibold mb-3">{i18n.t("final.cd624d178121")}</div>
                        {(!summary || (summary.upcoming_service.length === 0 && summary.overdue_service.length === 0)) && (
                            <div className="text-sm text-muted-foreground py-4">{i18n.t("final.cefb1df1fe3d")}</div>
                        )}
                        <div className="space-y-2">
                            {summary?.overdue_service.map((a) => (
                                <div key={a.id} className="flex items-center justify-between border border-destructive/30 bg-destructive/5 rounded-lg px-3 py-2" data-testid={`overdue-service-${a.id}`}>
                                    <div>
                                        <div className="font-semibold text-sm">{a.name}</div>
                                        <div className="text-xs text-muted-foreground capitalize">{a.type}</div>
                                    </div>
                                    <Badge variant="destructive">{i18n.t("final.fea8990fd0a9")} {a.next_service_date}</Badge>
                                </div>
                            ))}
                            {summary?.upcoming_service.map((a) => (
                                <div key={a.id} className="flex items-center justify-between border border-amber-500/30 bg-amber-500/5 rounded-lg px-3 py-2" data-testid={`upcoming-service-${a.id}`}>
                                    <div>
                                        <div className="font-semibold text-sm">{a.name}</div>
                                        <div className="text-xs text-muted-foreground capitalize">{a.type}</div>
                                    </div>
                                    <Badge className="bg-amber-500 text-white">{i18n.t("final.145caf292855")} {a.next_service_date}</Badge>
                                </div>
                            ))}
                        </div>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* DIALOGS */}
            {projectDialog && (
                <ProjectDialog
                    initial={projectDialog}
                    contractors={contractors}
                    onSave={(b) => saveDoc("construction-projects", b, setProjectDialog)}
                    onClose={() => setProjectDialog(null)}
                />
            )}
            {assetDialog && (
                <AssetDialog
                    initial={assetDialog}
                    onSave={(b) => saveDoc("farm-assets", b, setAssetDialog)}
                    onClose={() => setAssetDialog(null)}
                />
            )}
            {contractorDialog && (
                <ContractorDialog
                    initial={contractorDialog}
                    onSave={(b) => saveDoc("contractors", b, setContractorDialog)}
                    onClose={() => setContractorDialog(null)}
                />
            )}
            {expenseDialog && (
                <ExpenseDialog
                    initial={expenseDialog}
                    contractors={contractors}
                    onSave={(b) => saveDoc("project-expenses", b, setExpenseDialog)}
                    onClose={() => setExpenseDialog(null)}
                />
            )}
            {updateDialog && (
                <UpdateDialog
                    initial={updateDialog}
                    onSave={(b) => saveDoc("project-updates", b, setUpdateDialog)}
                    onClose={() => setUpdateDialog(null)}
                />
            )}
            {projectDetail && (
                <ProjectDetailDialog
                    project={projectDetail}
                    expenses={expenses.filter((e) => e.project_id === projectDetail.id)}
                    updates={updates.filter((u) => u.project_id === projectDetail.id)}
                    contractorMap={contractorMap}
                    currency={cur}
                    onAddExpense={() => setExpenseDialog({ project_id: projectDetail.id, date: farmDateIso() })}
                    onAddUpdate={() => setUpdateDialog({ project_id: projectDetail.id, date: farmDateIso() })}
                    onDeleteExpense={(id) => deleteDoc("project-expenses", id, "expense").then(() => setProjectDetail({ ...projectDetail }))}
                    onDeleteUpdate={(id) => deleteDoc("project-updates", id, "update").then(() => setProjectDetail({ ...projectDetail }))}
                    onClose={() => setProjectDetail(null)}
                />
            )}
        </div>
    );
}

// ---------- Assets tab (status/type/service filters) ----------
function AssetsTabContent({ assets, summary, cur, onNew, onEdit, onDelete, onQuickStatus }) {
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("all");
    const [type, setType] = useState("all");

    const byStatus = summary?.assets_by_status || {};
    const byType = summary?.assets_by_type || {};

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        return assets.filter((a) => {
            const st = a.status || "in_use";
            if (status !== "all" && st !== status) return false;
            if (type !== "all" && (a.type || "other") !== type) return false;
            if (term && ![a.name, a.location, a.notes, a.type, st]
                .some((v) => (v || "").toLowerCase().includes(term))) return false;
            return true;
        });
    }, [assets, q, status, type]);

    // Group filtered by type for a categorized view
    const grouped = useMemo(() => {
        const g = {};
        filtered.forEach((a) => {
            const t = a.type || "other";
            (g[t] = g[t] || []).push(a);
        });
        return g;
    }, [filtered]);

    return (
        <>
            {/* Status KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="assets-status-kpis">
                {ASSET_STATUS.map((s) => {
                    const count = byStatus[s.key] || 0;
                    const active = status === s.key;
                    return (
                        <button
                            key={s.key}
                            onClick={() => setStatus(active ? "all" : s.key)}
                            className={`text-left rounded-xl border p-3 transition-all ${s.color} ${active ? "ring-2 ring-offset-1 ring-primary" : ""}`}
                            data-testid={`assets-status-chip-${s.key}`}
                        >
                            <div className="tiny-label opacity-80">{s.label}</div>
                            <div className="text-2xl font-bold leading-tight mt-0.5">{count}</div>
                        </button>
                    );
                })}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={i18n.t("final.18dc84705bac")}
                    className="max-w-xs"
                    data-testid="assets-search-input"
                />
                <Select value={type} onValueChange={setType}>
                    <SelectTrigger className="w-56" data-testid="assets-type-filter">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{i18n.t("final.060be00f4f26")}</SelectItem>
                        {ASSET_TYPES.map((t) => (
                            <SelectItem key={t} value={t} className="capitalize">
                                {t.replace(/_/g, " ")} ({byType[t] || 0})
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {(status !== "all" || type !== "all" || q) && (
                    <Button size="sm" variant="ghost" onClick={() => { setStatus("all"); setType("all"); setQ(""); }} data-testid="assets-clear-filters">
                        {i18n.t("final.412226715c56")}
                    </Button>
                )}
                <div className="flex-1" />
                <Button onClick={onNew} data-testid="new-asset-btn">
                    <Plus className="w-4 h-4 mr-1" />{i18n.t("final.08b99c5d3e8f")}
                </Button>
            </div>

            <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                            <tr className="text-left">
                                <Th>{i18n.t("final.709a23220f2c")}</Th><Th>{i18n.t("final.bae7d5be7082")}</Th><Th>{i18n.t("final.d219c68101f5")}</Th><Th>{i18n.t("final.2f4979324e03")}</Th>
                                <Th>{i18n.t("final.747876156da0")}</Th><Th>{i18n.t("final.8fba6b663578")}</Th><Th></Th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 && (
                                <tr><td colSpan={7} className="text-center text-muted-foreground py-6" data-testid="assets-empty">
                                    {assets.length === 0 ? "No assets tracked yet. Click 'New Asset' to add one." : "No assets match the current filters."}
                                </td></tr>
                            )}
                            {Object.entries(grouped).map(([t, list]) => (
                                <React.Fragment key={t}>
                                    <tr className="bg-muted/30 border-t border-border">
                                        <td colSpan={7} className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-testid={`assets-group-${t}`}>
                                            {t.replace(/_/g, " ")} <span className="text-muted-foreground/70">· {list.length}</span>
                                        </td>
                                    </tr>
                                    {list.map((a) => <AssetRow key={a.id} a={a} cur={cur} onEdit={onEdit} onDelete={onDelete} onQuickStatus={onQuickStatus} />)}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </>
    );
}

function AssetRow({ a, cur, onEdit, onDelete, onQuickStatus }) {
    const d = daysUntil(a.next_service_date);
    const overdue = d != null && d < 0;
    const soon = d != null && d >= 0 && d <= 14;
    const sm = assetStatusMeta(a.status);
    return (
        <tr className="border-t border-border hover:bg-muted/30" data-testid={`asset-row-${a.id}`}>
            <td className="p-2">
                <div className="font-medium">{a.name}</div>
                <div className="text-xs text-muted-foreground capitalize">{(a.type || "other").replace(/_/g, " ")}</div>
            </td>
            <td className="p-2">
                <Select value={a.status || "in_use"} onValueChange={(v) => onQuickStatus(a, v)}>
                    <SelectTrigger className={`h-7 text-xs w-32 ${sm.color}`} data-testid={`asset-status-select-${a.id}`}>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {ASSET_STATUS.map((s) => (
                            <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </td>
            <td className="p-2 text-muted-foreground">{a.location || "—"}</td>
            <td className="p-2"><Badge variant="outline" className="capitalize">{a.condition || "unknown"}</Badge></td>
            <td className="p-2 font-mono">{formatCurrency(a.purchase_cost, cur)}</td>
            <td className="p-2">
                {a.next_service_date ? (
                    <span className={`text-xs ${overdue ? "text-destructive font-bold" : soon ? "text-amber-700 font-semibold" : "text-muted-foreground"}`}>
                        {a.next_service_date}{overdue ? " (overdue)" : soon ? ` (${d}d)` : ""}
                    </span>
                ) : <span className="text-muted-foreground text-xs">—</span>}
            </td>
            <td className="p-2 text-right whitespace-nowrap">
                <Button size="sm" variant="ghost" onClick={() => onEdit(a)} data-testid={`edit-asset-${a.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onDelete(a)} data-testid={`delete-asset-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </td>
        </tr>
    );
}

// ---------- Small components ----------
const KPI_TONES = {
    blue: "bg-blue-500/10 text-blue-700 border-blue-500/30",
    green: "bg-primary/10 text-primary border-primary/30",
    amber: "bg-amber-500/10 text-amber-700 border-amber-500/30",
    red: "bg-destructive/10 text-destructive border-destructive/40",
    slate: "bg-secondary text-foreground border-border",
};
function KpiCard({ icon: Icon, label, value, testid, tone = "slate", sub }) {
    return (
        <Card className={`p-3 border ${KPI_TONES[tone]}`} data-testid={testid}>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-80">
                <Icon className="w-3.5 h-3.5" />{label}
            </div>
            <div className="text-2xl font-bold mt-1">{value}</div>
            {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
        </Card>
    );
}
function Th({ children }) { return <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold">{children}</th>; }

// ---------- Project dialog ----------
function ProjectDialog({ initial, contractors, onSave, onClose }) {
    const [f, setF] = useState({
        name: "", type: "renovation", status: "planning", location: "",
        budget: "", start_date: "", target_date: "", contractor_id: "", description: "",
        ...initial,
    });
    const set = (k, v) => setF({ ...f, [k]: v });
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent data-testid="project-dialog" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{f.id ? "Edit Project" : "New Project"}</DialogTitle>
                    <DialogDescription>{i18n.t("final.5d597153857a")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <LabeledInput label={i18n.t("final.57162c35dea4")} value={f.name} onChange={(v) => set("name", v)} testid="project-name-input" />
                    <div className="grid grid-cols-2 gap-2">
                        <LabeledSelect label={i18n.t("final.3deb74565196")} value={f.type} onChange={(v) => set("type", v)} options={PROJECT_TYPES} testid="project-type-select" />
                        <LabeledSelect label={i18n.t("final.bae7d5be7082")} value={f.status} onChange={(v) => set("status", v)} options={PROJECT_STATUS.map((s) => s.key)} testid="project-status-select" />
                    </div>
                    <LabeledInput label={i18n.t("final.d219c68101f5")} value={f.location} onChange={(v) => set("location", v)} placeholder={i18n.t("final.f12d9541e2f3")} />
                    <div className="grid grid-cols-2 gap-2">
                        <LabeledInput label={i18n.t("final.9d7ab1a55796")} type="date" value={f.start_date} onChange={(v) => set("start_date", v)} />
                        <LabeledInput label={i18n.t("final.befd12a53600")} type="date" value={f.target_date} onChange={(v) => set("target_date", v)} testid="project-target-input" />
                    </div>
                    <LabeledInput label={i18n.t("final.19f4a18f9f8b")} type="number" value={f.budget} onChange={(v) => set("budget", v)} testid="project-budget-input" />
                    <LabeledSelect
                        label={i18n.t("final.a2876d155984")}
                        value={f.contractor_id || "__none__"}
                        onChange={(v) => set("contractor_id", v === "__none__" ? "" : v)}
                        options={[{ v: "__none__", l: "— None —" }, ...contractors.map((c) => ({ v: c.id, l: c.name }))]}
                    />
                    <div>
                        <div className="text-xs text-muted-foreground mb-1">{i18n.t("final.55f8ebc805e6")}</div>
                        <Textarea value={f.description || ""} onChange={(e) => set("description", e.target.value)} rows={3} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{i18n.t("final.77dfd2135f4d")}</Button>
                    <Button onClick={() => f.name.trim() ? onSave(f) : toast.error("Name required")} data-testid="save-project-btn">{i18n.t("final.efc007a393f6")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Asset dialog ----------
function AssetDialog({ initial, onSave, onClose }) {
    const [f, setF] = useState({
        name: "", type: "machinery", status: "in_use", location: "", condition: "good",
        purchase_date: "", purchase_cost: "", warranty_expiry: "",
        next_service_date: "", service_interval_days: "", notes: "",
        ...initial,
    });
    const set = (k, v) => setF({ ...f, [k]: v });
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent data-testid="asset-dialog" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{f.id ? "Edit Asset" : "New Asset"}</DialogTitle>
                    <DialogDescription>{i18n.t("final.b3d95022b903")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <LabeledInput label={i18n.t("final.57162c35dea4")} value={f.name} onChange={(v) => set("name", v)} testid="asset-name-input" />
                    <div className="grid grid-cols-3 gap-2">
                        <LabeledSelect label={i18n.t("final.a3c686e711e4")} value={f.type} onChange={(v) => set("type", v)} options={ASSET_TYPES} testid="asset-type-select" />
                        <LabeledSelect label={i18n.t("final.bae7d5be7082")} value={f.status || "in_use"} onChange={(v) => set("status", v)} options={ASSET_STATUS.map((s) => ({ v: s.key, l: s.label }))} testid="asset-status-select" />
                        <LabeledSelect label={i18n.t("final.2f4979324e03")} value={f.condition} onChange={(v) => set("condition", v)} options={ASSET_CONDITION} />
                    </div>
                    <LabeledInput label={i18n.t("final.d219c68101f5")} value={f.location} onChange={(v) => set("location", v)} placeholder={i18n.t("final.a229be11aa83")} />
                    <div className="grid grid-cols-2 gap-2">
                        <LabeledInput label={i18n.t("final.74feb2cafbea")} type="date" value={f.purchase_date} onChange={(v) => set("purchase_date", v)} />
                        <LabeledInput label={i18n.t("final.1a2309aff4b1")} type="number" value={f.purchase_cost} onChange={(v) => set("purchase_cost", v)} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <LabeledInput label={i18n.t("final.2d2a1342f45d")} type="date" value={f.next_service_date} onChange={(v) => set("next_service_date", v)} testid="asset-service-date-input" />
                        <LabeledInput label={i18n.t("final.51f98f12a890")} type="number" value={f.service_interval_days} onChange={(v) => set("service_interval_days", v)} />
                    </div>
                    <LabeledInput label={i18n.t("final.daa495690e5b")} type="date" value={f.warranty_expiry} onChange={(v) => set("warranty_expiry", v)} />
                    <div>
                        <div className="text-xs text-muted-foreground mb-1">{i18n.t("final.70440046a3dc")}</div>
                        <Textarea value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{i18n.t("final.77dfd2135f4d")}</Button>
                    <Button onClick={() => f.name.trim() ? onSave(f) : toast.error("Name required")} data-testid="save-asset-btn">{i18n.t("final.efc007a393f6")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Contractor dialog ----------
function ContractorDialog({ initial, onSave, onClose }) {
    const [f, setF] = useState({
        name: "", specialty: "", phone: "", email: "", address: "",
        rating: 5, notes: "", active: true, ...initial,
    });
    const set = (k, v) => setF({ ...f, [k]: v });
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent data-testid="contractor-dialog" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{f.id ? "Edit Contractor" : "New Contractor"}</DialogTitle>
                    <DialogDescription>{i18n.t("final.2a8aef209cf4")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <LabeledInput label={i18n.t("final.57162c35dea4")} value={f.name} onChange={(v) => set("name", v)} testid="contractor-name-input" />
                    <LabeledInput label={i18n.t("final.a98926213d84")} value={f.specialty} onChange={(v) => set("specialty", v)} placeholder={i18n.t("final.00d4dbbb759a")} />
                    <div className="grid grid-cols-2 gap-2">
                        <LabeledInput label={i18n.t("final.77064d526523")} value={f.phone} onChange={(v) => set("phone", v)} testid="contractor-phone-input" />
                        <LabeledInput label={i18n.t("final.84add5b29527")} value={f.email} onChange={(v) => set("email", v)} />
                    </div>
                    <LabeledInput label={i18n.t("final.d70f93df5e8f")} value={f.address} onChange={(v) => set("address", v)} />
                    <LabeledInput label={i18n.t("final.5ee99df097d2")} type="number" min="1" max="5" value={f.rating} onChange={(v) => set("rating", Number(v))} />
                    <div>
                        <div className="text-xs text-muted-foreground mb-1">{i18n.t("final.70440046a3dc")}</div>
                        <Textarea value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{i18n.t("final.77dfd2135f4d")}</Button>
                    <Button onClick={() => f.name.trim() ? onSave(f) : toast.error("Name required")} data-testid="save-contractor-btn">{i18n.t("final.efc007a393f6")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Expense dialog ----------
function ExpenseDialog({ initial, contractors, onSave, onClose }) {
    const [f, setF] = useState({
        project_id: "", date: farmDateIso(),
        category: "materials", description: "", amount: "",
        contractor_id: "", receipt_ref: "", ...initial,
    });
    const set = (k, v) => setF({ ...f, [k]: v });
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent data-testid="expense-dialog" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{f.id ? "Edit Expense" : "Log Expense"}</DialogTitle>
                    <DialogDescription>{i18n.t("final.dbc0dcff747b")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <LabeledSelect label={i18n.t("final.a3c686e711e4")} value={f.category} onChange={(v) => set("category", v)} options={EXPENSE_CATEGORIES} testid="expense-category-select" />
                        <LabeledInput label={i18n.t("final.eb9a4bc1c0c1")} type="date" value={f.date} onChange={(v) => set("date", v)} />
                    </div>
                    <LabeledInput label={i18n.t("final.416a07cf4e79")} value={f.description} onChange={(v) => set("description", v)} testid="expense-desc-input" />
                    <LabeledInput label={i18n.t("final.032e57accfe1")} type="number" value={f.amount} onChange={(v) => set("amount", v)} testid="expense-amount-input" />
                    <LabeledSelect
                        label={i18n.t("final.788e8718ca16")}
                        value={f.contractor_id || "__none__"}
                        onChange={(v) => set("contractor_id", v === "__none__" ? "" : v)}
                        options={[{ v: "__none__", l: "— None —" }, ...contractors.map((c) => ({ v: c.id, l: c.name }))]}
                    />
                    <LabeledInput label={i18n.t("final.524d1b0e5963")} value={f.receipt_ref} onChange={(v) => set("receipt_ref", v)} />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{i18n.t("final.77dfd2135f4d")}</Button>
                    <Button
                        onClick={() => (f.description.trim() && Number(f.amount) > 0
                            ? onSave(f)
                            : toast.error("Description and positive amount required"))}
                        data-testid="save-expense-btn"
                    >{i18n.t("final.efc007a393f6")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Update / progress log dialog ----------
function UpdateDialog({ initial, onSave, onClose }) {
    const [f, setF] = useState({
        project_id: "", date: farmDateIso(),
        progress_pct: 0, note: "", ...initial,
    });
    const set = (k, v) => setF({ ...f, [k]: v });
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent data-testid="update-dialog">
                <DialogHeader>
                    <DialogTitle>{i18n.t("final.aa9b6a90f790")}</DialogTitle>
                    <DialogDescription>{i18n.t("final.05375be4bc00")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <LabeledInput label={i18n.t("final.eb9a4bc1c0c1")} type="date" value={f.date} onChange={(v) => set("date", v)} />
                        <LabeledInput label={i18n.t("final.0da45a691d78")} type="number" min="0" max="100" value={f.progress_pct} onChange={(v) => set("progress_pct", Number(v))} testid="update-progress-input" />
                    </div>
                    <div>
                        <div className="text-xs text-muted-foreground mb-1">{i18n.t("final.2c924e308820")}</div>
                        <Textarea value={f.note} onChange={(e) => set("note", e.target.value)} rows={4} data-testid="update-note-input" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{i18n.t("final.77dfd2135f4d")}</Button>
                    <Button onClick={() => f.note.trim() ? onSave(f) : toast.error("Note required")} data-testid="save-update-btn">{i18n.t("final.efc007a393f6")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Project detail dialog ----------
function ProjectDetailDialog({ project, expenses, updates, contractorMap, currency, onAddExpense, onAddUpdate, onDeleteExpense, onDeleteUpdate, onClose }) {
    const budget = Number(project.budget) || 0;
    const spent = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
    const pct = budget > 0 ? Math.min(100, (spent / budget) * 100) : 0;
    const latestProgress = updates.reduce((max, u) => Math.max(max, Number(u.progress_pct || 0)), 0);
    const st = statusMeta(project.status);
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`project-detail-${project.id}`}>
                <DialogHeader>
                    <div className="flex items-center gap-2 flex-wrap">
                        <DialogTitle>{project.name}</DialogTitle>
                        <Badge variant="outline" className={st.color}>{st.label}</Badge>
                    </div>
                    <DialogDescription>
                        {project.location || "—"} · {project.type || "project"}
                        {project.contractor_id && contractorMap[project.contractor_id] && (
                            <> {i18n.t("final.7d8f801f3a78")} <b>{contractorMap[project.contractor_id].name}</b></>
                        )}
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-3 gap-2 text-center">
                    <Card className="p-3"><div className="tiny-label">{i18n.t("final.7aeba4cd15b1")}</div><div className="font-bold">{formatCurrency(budget, currency, { compact: true })}</div></Card>
                    <Card className="p-3"><div className="tiny-label">{i18n.t("final.c0d3c936adcd")}</div><div className={`font-bold ${spent > budget && budget > 0 ? "text-destructive" : ""}`}>{formatCurrency(spent, currency, { compact: true })}</div></Card>
                    <Card className="p-3"><div className="tiny-label">{i18n.t("final.1b90271d66cf")}</div><div className="font-bold">{latestProgress}%</div></Card>
                </div>
                <Progress value={pct} className={spent > budget && budget > 0 ? "[&>*]:bg-destructive" : ""} />

                {project.description && <div className="text-sm text-muted-foreground whitespace-pre-line border rounded-lg p-3 bg-muted/30">{project.description}</div>}

                <div className="flex items-center justify-between mt-2">
                    <h4 className="font-semibold text-sm">{i18n.t("final.10fcd25bf442")}{expenses.length})</h4>
                    <Button size="sm" onClick={onAddExpense} data-testid="detail-add-expense-btn"><Plus className="w-3.5 h-3.5 mr-1" />{i18n.t("final.f53d122b2016")}</Button>
                </div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                    {expenses.length === 0 && <div className="text-xs text-muted-foreground py-2">{i18n.t("final.149c06e3f613")}</div>}
                    {expenses.map((e) => (
                        <div key={e.id} className="flex items-center justify-between text-sm border rounded-md px-2 py-1.5">
                            <div className="min-w-0">
                                <div className="truncate"><b>{e.description}</b></div>
                                <div className="text-xs text-muted-foreground">{e.date} · {e.category}{e.contractor_id && contractorMap[e.contractor_id] ? ` · ${contractorMap[e.contractor_id].name}` : ""}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="font-mono font-semibold">{formatCurrency(e.amount, currency)}</span>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => onDeleteExpense(e.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex items-center justify-between mt-2">
                    <h4 className="font-semibold text-sm">{i18n.t("final.0bdb018de927")}{updates.length})</h4>
                    <Button size="sm" onClick={onAddUpdate} data-testid="detail-add-update-btn"><Plus className="w-3.5 h-3.5 mr-1" />{i18n.t("final.17087666e54c")}</Button>
                </div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                    {updates.length === 0 && <div className="text-xs text-muted-foreground py-2">{i18n.t("final.24f53d5101fb")}</div>}
                    {updates.map((u) => (
                        <div key={u.id} className="flex items-start justify-between text-sm border rounded-md px-2 py-1.5">
                            <div className="min-w-0 flex-1">
                                <div className="text-xs text-muted-foreground">{u.date} · <b>{u.progress_pct}%</b></div>
                                <div className="text-sm whitespace-pre-line">{u.note}</div>
                            </div>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => onDeleteUpdate(u.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Labeled form inputs ----------
function LabeledInput({ label, value, onChange, type = "text", placeholder, testid, min, max }) {
    return (
        <div>
            <div className="text-xs text-muted-foreground mb-1">{label}</div>
            <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} min={min} max={max} />
        </div>
    );
}
function LabeledSelect({ label, value, onChange, options, testid }) {
    const opts = options.map((o) => (typeof o === "string" ? { v: o, l: o.replace(/_/g, " ") } : o));
    return (
        <div>
            <div className="text-xs text-muted-foreground mb-1">{label}</div>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
                <SelectContent>
                    {opts.map((o) => (
                        <SelectItem key={o.v} value={o.v} className="capitalize">{o.l}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}
